import { useMemo } from 'react';
import type { DripDashboardSnapshot } from '../types';
import { DripSection } from './DripSection';

const DAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function DripEngagementHeatmap({ heatmap }: Pick<DripDashboardSnapshot, 'heatmap'>) {
  const { max, cells } = useMemo(() => {
    let m = 1;
    for (const c of heatmap) m = Math.max(m, c.value);
    return { max: m, cells: heatmap };
  }, [heatmap]);

  const hours = useMemo(() => {
    const hs = [...new Set(cells.map((c) => c.hour))].sort((a, b) => a - b);
    return hs;
  }, [cells]);

  return (
    <DripSection
      id="drip-heatmap"
      title="Engagement heatmap"
      description="Relative intensity by weekday and hour (local reporting time)."
    >
      <div className="overflow-x-auto" role="img" aria-label="Heatmap of engagement by day and hour">
        <div className="inline-block min-w-full">
          <div className="grid gap-0.5" style={{ gridTemplateColumns: `3.5rem repeat(${hours.length}, minmax(1.25rem,1fr))` }}>
            <div />
            {hours.map((h) => (
              <div key={h} className="text-center font-mono text-[0.6rem] text-[var(--home-muted-on-dark)]">
                {h}h
              </div>
            ))}
            {DAY.map((label, d) => (
              <div key={label} className="contents">
                <div className="flex items-center font-display text-[0.58rem] font-semibold uppercase tracking-wide text-[var(--home-muted-on-dark)]">
                  {label}
                </div>
                {hours.map((h) => {
                  const cell = cells.find((c) => c.dayIndex === d && c.hour === h);
                  const v = cell?.value ?? 0;
                  const o = max > 0 ? v / max : 0;
                  return (
                    <div
                      key={`${d}-${h}`}
                      className="aspect-square min-h-[1.25rem] rounded-sm ring-1 ring-white/[0.06]"
                      style={{
                        background: `rgba(56, 189, 248, ${0.12 + o * 0.75})`,
                      }}
                      title={`${label} ${h}:00 — intensity ${v}`}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </DripSection>
  );
}
